import { createContext, type ReactNode, useContext, useRef } from 'react';

import type { UseMutationResult } from '@zg-rentals/react-query5';

export type SaveRegistration<TVariables = unknown> = {
  mutation: UseMutationResult<unknown, unknown, TVariables, unknown>;
  /**
   * Build variables for the mutation. Return null to skip save (validation failed or unchanged).
   */
  buildVariables: () => TVariables | null;
  /**
   * Check if the field has a validation error.
   */
  hasError: () => boolean;
};

export type FormModeContextValue =
  | { mode: 'wizard' }
  | {
      mode: 'modal';
      registerSave: <TVariables = unknown>(fieldId: string, registration: SaveRegistration<TVariables>) => void;
      unregisterSave: (fieldId: string) => void;
      triggerAllSaves: () => Promise<{
        hasValidationErrors: boolean;
        savesExecuted: number;
        hasMutationErrors: boolean;
      }>;
    };

const FormModeContext = createContext<FormModeContextValue>({ mode: 'wizard' });

type FormModeProviderProps = {
  mode: 'wizard' | 'modal';
  children: ReactNode;
};

export const FormModeProvider = ({ mode, children }: FormModeProviderProps) => {
  // Save registry only created in modal mode
  const saveRegistry = useRef<Map<string, SaveRegistration<unknown>>>(new Map());

  const contextValue: FormModeContextValue =
    mode === 'wizard'
      ? { mode: 'wizard' }
      : {
          mode: 'modal',
          registerSave: <TVariables,>(fieldId: string, registration: SaveRegistration<TVariables>) => {
            saveRegistry.current.set(fieldId, registration as SaveRegistration<unknown>);
          },
          unregisterSave: (fieldId: string) => {
            saveRegistry.current.delete(fieldId);
          },
          triggerAllSaves: async () => {
            const registrations = Array.from(saveRegistry.current.values());

            // Check for validation errors BEFORE executing any saves
            const hasValidationErrors = registrations.some((registration) => registration.hasError());

            if (hasValidationErrors) {
              return { hasValidationErrors: true, savesExecuted: 0, hasMutationErrors: false };
            }

            // Build variables and group by mutation + variables signature
            const uniqueSaves = new Map<
              string,
              { mutation: UseMutationResult<unknown, unknown, unknown, unknown>; variables: unknown }
            >();

            for (const registration of registrations) {
              const variables = registration.buildVariables();

              // Skip if buildVariables returns null (validation failed or unchanged)
              if (variables === null) {
                continue;
              }

              // Create a unique key based on mutation identity + serialized variables
              const mutationId = (registration.mutation as { mutationId?: number }).mutationId ?? registration.mutation;
              const key = JSON.stringify({ mutationId, variables });

              // Only keep one save per unique mutation + variables combination
              uniqueSaves.set(key, { mutation: registration.mutation, variables });
            }

            const savesExecuted = uniqueSaves.size;

            // Execute unique saves in parallel, catching per-save errors to prevent modal close
            let hasMutationErrors = false;
            await Promise.allSettled(
              Array.from(uniqueSaves.values()).map(async ({ mutation, variables }) => {
                try {
                  await mutation.mutateAsync(variables);
                } catch {
                  hasMutationErrors = true;
                }
              }),
            );

            return { hasValidationErrors: false, savesExecuted, hasMutationErrors };
          },
        };

  return <FormModeContext.Provider value={contextValue}>{children}</FormModeContext.Provider>;
};

export const useFormMode = () => {
  return useContext(FormModeContext);
};
